import { Controller } from "@hotwired/stimulus"

// A list of free-text values entered as removable chips (components/chip_input), such as a
// member's credentials (TMS-1760). Enter, a comma or picking a suggestion adds what was typed;
// leaving the field or submitting the form adds it too, so nothing typed is lost. Backspace in
// the empty field removes the last chip. Each chip carries its own hidden input, so the form
// posts the list as name[]. Fires `chip-input:change` with the current values.
export default class extends Controller {
  static targets = ["field", "list", "template", "status"]
  static values = {
    name: String,
    maxItems: { type: Number, default: 10 },
    maxLength: { type: Number, default: 40 }
  }

  connect() {
    this.form = this.element.closest("form")
    this.onSubmit = () => this.commit()
    this.form?.addEventListener("submit", this.onSubmit)
    this.sync()
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.onSubmit)
  }

  focus(event) {
    if (event.target.closest("button")) return
    this.fieldTarget.focus()
  }

  keydown(event) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault()
      this.commit()
    } else if (event.key === "Backspace" && this.fieldTarget.value === "") {
      const chips = this.chips()
      if (chips.length) this.removeChip(chips[chips.length - 1])
    }
  }

  // A suggestion picked from the datalist arrives as one replacement, not as typing.
  input(event) {
    if (event.inputType === "insertReplacementText" || event.inputType === undefined) this.commit()
  }

  commit() {
    const text = this.fieldTarget.value.split(",").map((part) => part.replace(/\s+/g, " ").trim()).filter(Boolean)
    this.fieldTarget.value = ""
    if (!text.length) return

    text.forEach((value) => this.add(value.slice(0, this.maxLengthValue)))
    this.sync()
  }

  remove(event) {
    event.preventDefault()
    this.removeChip(event.currentTarget.closest("[data-chip]"))
    this.fieldTarget.focus()
  }

  values() {
    return this.chips().map((chip) => chip.dataset.chip)
  }

  // private

  add(value) {
    const taken = this.values().some((existing) => existing.toLowerCase() === value.toLowerCase())
    if (taken || this.chips().length >= this.maxItemsValue) return

    const fragment = this.templateTarget.content.cloneNode(true)
    const chip = fragment.querySelector("[data-chip]")
    chip.dataset.chip = value
    chip.querySelector("[data-chip-label]").textContent = value
    chip.querySelector("button").setAttribute("aria-label", `Remove ${value}`)
    const hidden = chip.querySelector("input[type=hidden]")
    hidden.name = this.nameValue
    hidden.value = value
    this.listTarget.appendChild(fragment)
    this.announce(`${value} added`)
  }

  removeChip(chip) {
    if (!chip) return
    const value = chip.dataset.chip
    chip.remove()
    this.announce(`${value} removed`)
    this.sync()
  }

  chips() {
    return Array.from(this.listTarget.querySelectorAll("[data-chip]"))
  }

  sync() {
    const full = this.chips().length >= this.maxItemsValue
    this.fieldTarget.disabled = full
    this.listTarget.hidden = this.chips().length === 0
    this.element.dispatchEvent(new CustomEvent("chip-input:change", { bubbles: true, detail: { values: this.values() } }))
  }

  announce(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }
}
